<script setup lang="ts">
import { Info } from "@lucide/vue";
import InfiniteLoading from "v3-infinite-loading";
import type { StateHandler } from "v3-infinite-loading/lib/types";
import type Zod from "zod";

import type {
	CorpusSearch,
	CorpusSearchHTML,
	Div,
	HttpResponse,
	RFC7807Problem,
} from "@/lib/api-client";
import { useTeiHeadersStore } from "@/stores/use-tei-headers-store.ts";
import type { CorpusQuerySchema } from "@/types/global.ts";
import { getCorpusHitContext } from "@/utils/corpus-hit-context.ts";

function isCorpusSearchOrHtml(item: unknown): item is CorpusSearch | CorpusSearchHTML {
	return Object.prototype.hasOwnProperty.call(item, "hits");
}

function isCorpusSearch(item: unknown): item is CorpusSearch {
	return (
		isCorpusSearchOrHtml(item) &&
		(Object.prototype.hasOwnProperty.call(item.hits, "divs") ||
			Object.prototype.hasOwnProperty.call(item.hits, "div"))
	);
}

const api = useApiClient();
const { simpleItems } = useTeiHeadersStore();
const props = defineProps<{ params: Zod.infer<typeof CorpusQuerySchema>["params"] }>();
const emit = defineEmits<{
	updateQueryParam: [queryString: string];
}>();
const queryString = ref(props.params.queryString);
const hits = ref<Array<Div & { label?: string; teiSource?: string }>>([]);
const includeContentWarnings = ref(false);
const contentWarningsId = useId();
const visibleHits = computed(() => {
	if (includeContentWarnings.value) return hits.value;
	const warnedIds = new Set(
		simpleItems.filter((header) => header.triggerWarnings?.length).map((header) => header.id),
	);
	return hits.value.filter((hit) => !hit["@docRef"] || !warnedIds.has(hit["@docRef"]));
});
const scrollCursor = ref({ count: 0, page: 1, pageSize: 30, complete: false });
const showHelp = ref<boolean>(false);
const isSearching = ref(false);
const isLoadingMore = ref(false);

const inlineLinguisticAnnotations = ref<false | true | "indeterminate">(true);
const inlineTranslations = ref<false | true | "indeterminate">(true);
const words: Ref<Array<string>> = ref([]);

const lastRestoredQueryString = ref<string>();
const {
	hasInlineTranslations: blockHasInlineTranslations,
	hasLinguisticAnnotations: blockHasLinguisticAnnotations,
} = useCorpusAnnotationAvailability();

const hasLinguisticAnnotations = computed(() => {
	return blockHasLinguisticAnnotations(visibleHits.value);
});

const hasInlineTranslations = computed(() => {
	return blockHasInlineTranslations(visibleHits.value);
});

const showLinguisticAnnotations = computed(() => {
	return hasLinguisticAnnotations.value && inlineLinguisticAnnotations.value === true;
});

const showInlineTranslations = computed(() => {
	return hasInlineTranslations.value && inlineTranslations.value === true;
});

function enrichHitsWithLabels(items: Array<Div & { label?: string; teiSource?: string }>) {
	items.forEach((hit) => {
		const teiHeader = simpleItems.find((header) => header.id === hit["@docRef"]);
		hit.label = teiHeader?.label;
		hit.teiSource = teiHeader?.teiSource_idno;
	});
}

async function searchCorpus(options: { updateRoute?: boolean } = {}) {
	const { updateRoute = true } = options;
	isSearching.value = true;
	hits.value = [];
	scrollCursor.value.page = 1;
	let status = { isValid: true, warnings: [] as Array<string> };
	try {
		if (words.value.length > 0) queryString.value = `[word="${words.value.join("|")}"]`;
		if (updateRoute) emit("updateQueryParam", queryString.value);

		const result = await api.vicav.searchCorpus(
			{
				query: queryString.value.toString(),
				render: "json",
				page: scrollCursor.value.page,
				pageSize: scrollCursor.value.pageSize,
			},
			{ headers: { Accept: "application/json" } },
		);

		if (result.error) {
			console.error(result.error);
		}

		const newScrollCursor = scrollCursor.value;

		if (isCorpusSearch(result.data) && result.data.hits && !Array.isArray(result.data.hits)) {
			if (Array.isArray(result.data.hits?.divs)) hits.value = result.data.hits.divs;
			else if (result.data.hits?.div) hits.value.push(result.data.hits?.div);
			enrichHitsWithLabels(hits.value);
			newScrollCursor.count = result.data.hits["@count"];
			newScrollCursor.page = result.data.hits["@page"];
			newScrollCursor.pageSize = result.data.hits["@pageSize"];
			newScrollCursor.complete =
				result.data.hits["@page"] * result.data.hits["@pageSize"] >= result.data.hits["@count"];
		}
	} catch (e) {
		status = {
			isValid: false,
			warnings: [(e as HttpResponse<never, RFC7807Problem>).error.detail],
		};
		return status;
	} finally {
		isSearching.value = false;
	}
	return status;
}

async function loadMoreHits() {
	isLoadingMore.value = true;
	try {
		const nextPage = scrollCursor.value.page + 1;
		const result = await api.vicav.searchCorpus(
			{
				query: queryString.value.toString(),
				render: "json",
				page: nextPage,
				pageSize: scrollCursor.value.pageSize,
			},
			{ headers: { Accept: "application/json" } },
		);

		if (result.error) {
			console.error(result.error);
			throw new Error(result.error.detail ?? "Failed to load more results");
		}

		const newScrollCursor = scrollCursor.value;

		if (isCorpusSearch(result.data) && result.data.hits && !Array.isArray(result.data.hits)) {
			const newHits: Array<Div & { label?: string; teiSource?: string }> = [];
			if (Array.isArray(result.data.hits?.divs)) newHits.push(...result.data.hits.divs);
			else if (result.data.hits?.div) newHits.push(result.data.hits.div);
			enrichHitsWithLabels(newHits);
			newScrollCursor.count = result.data.hits["@count"];
			newScrollCursor.page = result.data.hits["@page"];
			newScrollCursor.pageSize = result.data.hits["@pageSize"];
			newScrollCursor.complete =
				result.data.hits["@page"] * result.data.hits["@pageSize"] >= result.data.hits["@count"];
			hits.value = hits.value.concat(newHits);
		} else {
			newScrollCursor.complete = true;
		}
	} finally {
		isLoadingMore.value = false;
	}
}

const handleInfiniteScroll = async function ($state: StateHandler) {
	try {
		await loadMoreHits();
		if (scrollCursor.value.complete) $state.complete();
		else $state.loaded();
	} catch (e) {
		console.error(e);
		$state.error();
	}
};

watch(
	() => props.params.queryString,
	(value) => {
		if (value === "" || value === lastRestoredQueryString.value) return;
		queryString.value = value;
		lastRestoredQueryString.value = value;
		if (!isSearching.value) void searchCorpus({ updateRoute: false });
	},
	{ flush: "post", immediate: true },
);

const openNewWindowFromAnchor = useAnchorClickHandler();

const { data: config } = useProjectInfo();
const specialCharacters = config.value?.projectConfig?.specialCharacters;
const wordSearch = ref("");
const dataWordsQuery = useDataWords({ dataType: "CorpusText", query: wordSearch });

const wordOptions = computed(() => {
	return ((dataWordsQuery.data.value as unknown as Array<string>) ?? []).map((item: string) => {
		return { label: item, value: item };
	});
});

const autocompleteFetching = computed(() => dataWordsQuery.isFetching.value);

function getHitKey(hit: Div, index: number) {
	return [hit["@docRef"], hit["@id"], hit.hits?.join(","), index].filter(Boolean).join("-");
}

const displayHitContexts = computed(() =>
	visibleHits.value.map((hit) => ({ hit, context: getCorpusHitContext(hit) })),
);

const { cqlConfig: attributeConfig } = useCqlAttributes({
	corpname: config.value?.projectConfig?.noskeHost ?? "",
});

const cqlConfig = computed<CqlConfig>(() =>
	attributeConfig.value.map((attr) =>
		// `word` suggestions are fetched dynamically (driven by `wordSearch` below).
		attr.key === "word"
			? {
					...attr,
					values: wordOptions.value.map((o) => ({ value: o.value, displayValue: o.label })),
				}
			: attr,
	),
);
const { cqlTriggers } = useCqlTriggers(cqlConfig);
</script>

<template>
	<!-- eslint-disable vue/no-v-html -->
	<div class="p-2">
		<form
			class="block w-full rounded-sm border border-gray-300 bg-gray-50 p-2.5 px-4 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
		>
			<label class="mb-2 flex p-0 font-bold" for="word_tags">
				<span class="mr-2">Search for words or enter a CQL query</span>
				<a href="#" title="More information" @click="showHelp = !showHelp"
					><span class="hidden">More information</span>
					<Info class="size-4" />
				</a>
			</label>
			<div v-if="showHelp" class="mb-2 flex flex-col gap-2">
				<span class="text-gray-500"
					>Enter beginning of the word to trigger autocomplete suggestions from the words occurring
					in the corpus. Autocomplete is accent-insensitive, allowing for a simplified word form
					selection. Eg. "wa" will return results starting with "wā" or "ẉa" as well. <br />
					Apart from the accent-insensitivity, "?" is used as a shortcut for ʔalif or ʕayn.<br />
					Instead of choosing exact word forms from autocomplete results, you can enter a word with
					wildcards and add it to the queried words by pressing enter. Supported wildcards: ".?"
					stand for one character ".*" stands for multiple characters. characters.<br />
					Example: w.?n would yield results like "wen", "win", "w.*n" would yield results for "wen,
					win or weyn" as well.
				</span>
				<span class="text-gray-500"
					>Alternatively, enter a proper CQL query with exact transliateration characters. (<a
						class="content-center"
						href="https://campus.dariah.eu/resources/hosted/corpus-query-language-im-austrian-media-corpus"
						target="_blank"
						title="More information about CQL syntax"
						><span>More info</span></a
					>)
				</span>
			</div>
			<Searchbar
				v-model="queryString"
				v-model:search-term="wordSearch"
				:dynamic-keys="['word']"
				feature-trigger="["
				free-trigger-key="word"
				:is-loading="autocompleteFetching"
				:mode="props.params.mode"
				:on-submit="
					(v) => {
						if (!isSearching) {
							queryString = v;
							return searchCorpus();
						}
						return;
					}
				"
				query-mode="cql"
				:special-characters="specialCharacters"
				:triggers="cqlTriggers"
			/>
			<div class="mt-3 flex items-center gap-2">
				<Checkbox
					:id="contentWarningsId"
					:checked="includeContentWarnings"
					@update:checked="includeContentWarnings = $event === true"
				/>
				<label :for="contentWarningsId">Include texts with content warnings</label>
			</div>
		</form>
		<div
			v-if="hasLinguisticAnnotations || hasInlineTranslations"
			class="flex justify-end gap-3 p-4"
		>
			<div v-if="hasLinguisticAnnotations">
				<Checkbox
					id="switch-linguistic-annotations"
					:checked="inlineLinguisticAnnotations === true"
					@update:checked="inlineLinguisticAnnotations = $event"
				/>
				<label for="switch-linguistic-annotations">&nbsp;Linguistic annotations</label>
			</div>
			<div v-if="hasInlineTranslations">
				<Checkbox
					id="switch-translations"
					:checked="inlineTranslations === true"
					@update:checked="inlineTranslations = $event"
				/>
				<label for="switch-translations">&nbsp;Inline Translations</label>
			</div>
		</div>
		<div v-if="isSearching" class="flex justify-center py-4 text-primary">
			<LoadingIndicator>Loading corpus results...</LoadingIndicator>
		</div>
		<div v-if="hits && hits.length > 0">
			<div class="my-2 flex gap-2">
				<div>
					Query: <span class="font-mono text-header">{{ queryString }}</span>
				</div>
				<div>•</div>
				<div>
					{{ scrollCursor.count }} {{ scrollCursor.count > 1 ? "results" : "result" }} ({{
						visibleHits.length
					}}
					shown<span v-if="!includeContentWarnings && visibleHits.length < hits.length"
						>; {{ hits.length - visibleHits.length }} loaded results hidden due to content
						warnings</span
					>)
				</div>
			</div>
			<table>
				<tr
					v-for="({ hit, context }, hitIndex) in displayHitContexts"
					:key="getHitKey(hit, hitIndex)"
				>
					<td class="p-0">
						<a
							:data-hits="context.anchorHitId"
							data-target-type="CorpusText"
							:data-tei-source="hit.teiSource"
							:data-text-id="hit['@docRef']"
							:data-u="context.utteranceId"
							href="#"
							@click="openNewWindowFromAnchor"
						>
							<strong>{{ hit["@docRef"] }}</strong>
						</a>
					</td>
					<td>
						<div
							v-if="context.before.length || context.matches.length || context.after.length"
							class="overflow-x-auto px-6 py-3"
						>
							<div
								class="inline-grid min-w-full grid-cols-[minmax(max-content,1fr)_auto_minmax(max-content,1fr)] items-start gap-x-3"
							>
								<div class="flex flex-nowrap justify-end justify-self-end">
									<CorpusTextJsonUtterance
										v-for="(uContent, index) in context.before"
										:key="`before-${index}`"
										:hits="hit.hits"
										:inline-linguistic-annotation="showLinguisticAnnotations"
										:utterance="uContent"
									></CorpusTextJsonUtterance>
								</div>
								<div class="flex min-w-fit flex-nowrap justify-self-center">
									<CorpusTextJsonUtterance
										v-for="(uContent, index) in context.matches"
										:key="`match-${index}`"
										:hits="hit.hits"
										:inline-linguistic-annotation="showLinguisticAnnotations"
										:utterance="uContent"
									></CorpusTextJsonUtterance>
								</div>
								<div class="flex flex-nowrap justify-self-start">
									<CorpusTextJsonUtterance
										v-for="(uContent, index) in context.after"
										:key="`after-${index}`"
										:hits="hit.hits"
										:inline-linguistic-annotation="showLinguisticAnnotations"
										:utterance="uContent"
									></CorpusTextJsonUtterance>
								</div>
							</div>
						</div>
						<div
							v-if="showInlineTranslations && hit.Translation_spanGrp"
							class="flex max-w-full flex-row px-6 py-3 italic"
						>
							{{ hit.Translation_spanGrp.span["$"] }}
						</div>
					</td>
				</tr>
			</table>
			<InfiniteLoading v-if="!scrollCursor.complete" @infinite="handleInfiniteScroll" />
		</div>
	</div>
</template>
